/**
 * DIEUKYDIEU.TV - MOCK DATA ADAPTER MODULE
 * Simulates async API responses using existing in-memory mock datasets.
 * Guarantees zero regression and 100% data fidelity while in mock mode.
 * Under 300 lines rule compliant.
 */

import { API_CONFIG } from '../config.js';
import { mediaCategories, featuredMedia, videoList, audioMediaList, galleryList } from '../../media-data.js';
import { STORY_TOPICS_DATA, WEB_STORIES_DATA } from './story-mock-data.js';

function delay(ms) {
    if (!ms || ms <= 0) return Promise.resolve();
    return new Promise(resolve => setTimeout(resolve, ms));
}

function getRawSitemapData() {
    const root = typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : {});
    if (Array.isArray(root.SITEMAP_DATA) && root.SITEMAP_DATA.length > 0) {
        return root.SITEMAP_DATA;
    }
    return [];
}

function getRawSitemapTree() {
    const root = typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : {});
    if (Array.isArray(root.SITEMAP_TREE) && root.SITEMAP_TREE.length > 0) {
        return root.SITEMAP_TREE;
    }
    return [];
}

export const mockAdapter = {
    /**
     * Categories Tree
     */
    async getCategoriesTree() {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        return {
            success: true,
            data: getRawSitemapTree()
        };
    },

    /**
     * All Category Sections with sample articles
     */
    async getAllCategories() {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        return {
            success: true,
            data: getRawSitemapData()
        };
    },

    /**
     * Category by slug or ID with its articles
     */
    async getCategoryBySlug(slug) {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        if (!slug) return { success: false, data: null };
        const clean = slug.toLowerCase().trim();
        const data = getRawSitemapData();
        const found = data.find(s => s.slug === clean || String(s.id) === clean);
        return {
            success: !!found,
            data: found || null
        };
    },

    /**
     * Single Article by ID or Title
     */
    async getArticleById(idOrSlug) {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        const data = getRawSitemapData();
        const targetId = Number(idOrSlug);

        for (const cat of data) {
            if (!cat.articles) continue;
            for (const art of cat.articles) {
                if (art.id === targetId || String(art.id) === String(idOrSlug) || art.slug === idOrSlug) {
                    return {
                        success: true,
                        data: {
                            ...art,
                            category: { id: cat.id, name: cat.name, slug: cat.slug }
                        }
                    };
                }
            }
        }

        return {
            success: false,
            data: null,
            message: `Article not found with ID/Slug: ${idOrSlug}`
        };
    },

    /**
     * Paginated articles for a category
     */
    async getArticlesByCategory(categorySlug, page = 1, limit = 12) {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        const data = getRawSitemapData();
        const cat = data.find(s => s.slug === categorySlug || String(s.id) === categorySlug);
        const allArticles = cat && cat.articles ? cat.articles : [];

        const startIndex = (page - 1) * limit;
        const items = allArticles.slice(startIndex, startIndex + limit);

        return {
            success: true,
            data: {
                items,
                total: allArticles.length,
                page,
                limit,
                totalPages: Math.ceil(allArticles.length / limit) || 1
            }
        };
    },

    /**
     * Media Categories
     */
    async getMediaCategories() {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        return {
            success: true,
            data: mediaCategories
        };
    },

    /**
     * Featured Media Item
     */
    async getFeaturedMedia() {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        return {
            success: true,
            data: featuredMedia
        };
    },

    /**
     * Media items list filtered by type (video, audio, gallery) and subcategory
     */
    async getMediaItems(type = 'all', subCategory = 'all') {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        let items = [];
        if (type === 'video') items = [...videoList];
        else if (type === 'audio') items = [...audioMediaList];
        else if (type === 'gallery') items = [...galleryList];
        else items = [...videoList, ...audioMediaList, ...galleryList];

        if (subCategory && subCategory !== 'all') {
            items = items.filter(i => i.subCategory === subCategory);
        }

        return {
            success: true,
            data: items
        };
    },

    /**
     * Audio playlist for player
     */
    async getAudioPlaylist() {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        let playlist = [];
        const root = typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : {});
        if (Array.isArray(root.AUDIO_PLAYLIST)) {
            playlist = root.AUDIO_PLAYLIST;
        } else {
            // Default from audioMediaList
            playlist = audioMediaList;
        }
        return {
            success: true,
            data: playlist
        };
    },

    /**
     * Client-side search simulation over mock articles
     */
    async searchArticles(query, { category = 'all', page = 1, limit = 20 } = {}) {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        if (!query || !query.trim()) {
            return { success: true, data: { items: [], total: 0 } };
        }

        const q = query.toLowerCase().trim();
        const data = getRawSitemapData();
        const results = [];

        for (const cat of data) {
            if (category !== 'all' && cat.slug !== category && String(cat.id) !== category) {
                continue;
            }
            if (!cat.articles) continue;
            for (const art of cat.articles) {
                const title = (art.title || '').toLowerCase();
                const desc = (art.desc || '').toLowerCase();
                const tag = (art.tag || '').toLowerCase();
                if (title.includes(q) || desc.includes(q) || tag.includes(q)) {
                    results.push({
                        ...art,
                        category: { id: cat.id, name: cat.name, slug: cat.slug }
                    });
                }
            }
        }

        const startIndex = (page - 1) * limit;
        return {
            success: true,
            data: {
                items: results.slice(startIndex, startIndex + limit),
                total: results.length,
                query
            }
        };
    },

    /**
     * Topic Quiz dataset
     */
    async getTopicQuiz(topicId, lang = 'vi') {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        const root = typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : {});
        const quizzes = root.TOPIC_QUIZZES || null;
        if (!quizzes || !quizzes[topicId]) {
            return { success: false, data: null };
        }
        const topic = quizzes[topicId];
        const content = topic[lang] || topic['vi'] || {};
        return {
            success: true,
            data: {
                topicId,
                articleId: topic.articleId,
                ...content
            }
        };
    },

    /**
     * Home Carousel Story Topics
     */
    async getStoryTopics() {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        return {
            success: true,
            data: STORY_TOPICS_DATA
        };
    },

    /**
     * Web Story Slides by ID
     */
    async getWebStory(storyId, lang = 'vi') {
        await delay(API_CONFIG.MOCK_DELAY_MS);
        const story = WEB_STORIES_DATA[storyId] || null;
        return {
            success: !!story,
            data: story
        };
    }
};
